// A vídeo-aula da Travessia (01/11/2026).
//
// O vídeo sai da VPS como HLS adaptativo: master.m3u8 com 4 qualidades
// (360p a 1080p, vertical), em pedaços de 6 s com cache imutável no nginx
// (deploy/nginx/video-estatico.conf). O player escolhe a qualidade pela rede e
// pelo tamanho da tela, então muita gente assistindo ao mesmo tempo não trava
// ninguém: quem está no 4G cai para uma qualidade menor em vez de parar.
//
// Safari e o app do iPhone tocam HLS sozinhos. Nos outros, o hls.js vem de
// /vendor só na hora em que alguém abre um vídeo: não pesa no carregamento do app.
//
// O player abre numa camada por cima do mapa, do tamanho da tela, e tem o botão
// "Tela cheia" (no iPhone, o <video> entra no player nativo do sistema).
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const HLS_JS = '/vendor/hls-1.7.4/hls.light.min.js';
let carregandoHls: Promise<any> | null = null;

function carregarHls(): Promise<any> {
  const w = window as any;
  if (w.Hls) return Promise.resolve(w.Hls);
  if (!carregandoHls) {
    carregandoHls = new Promise((ok, falha) => {
      const s = document.createElement('script');
      s.src = HLS_JS; s.async = true;
      s.onload = () => ok(w.Hls);
      s.onerror = () => { carregandoHls = null; falha(new Error('não carregou o hls.js')); };
      document.head.appendChild(s);
    });
  }
  return carregandoHls;
}

/** O vídeo do próprio site vira caminho relativo: o CSP (connect-src 'self') só
 *  deixa o hls.js buscar na mesma origem, e o staging (:5000) serve o mesmo /video/. */
function doSite(url: string): string {
  return url.replace(/^https:\/\/forjamente\.com\.br(?=\/video\/)/, '');
}

/** A capa mora ao lado do master.m3u8, com o nome poster.jpg (o encode-hls.sh grava as duas). */
export function capaDoVideo(url: string): string | null {
  return /\/master\.m3u8$/.test(url) ? doSite(url).replace(/master\.m3u8$/, 'poster.jpg') : null;
}

export function PlayerTelaCheia({ url, titulo, onFechar }: { url: string; titulo: string; onFechar: () => void }) {
  const caixa = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [erro, setErro] = useState(false);
  const [cheia, setCheia] = useState(false);

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    const src = doSite(url);
    let hls: any = null;
    let vivo = true;
    if (v.canPlayType('application/vnd.apple.mpegurl')) {
      v.src = src;
      v.play().catch(() => {});
    } else {
      carregarHls().then((Hls) => {
        if (!vivo) return;
        if (!Hls || !Hls.isSupported()) { setErro(true); return; }
        // enableWorker: false — o CSP não deixa criar worker de blob:, e fMP4 não precisa.
        hls = new Hls({ enableWorker: false, capLevelToPlayerSize: true, maxBufferLength: 30, maxMaxBufferLength: 60 });
        hls.on(Hls.Events.ERROR, (_e: unknown, d: any) => {
          if (!d.fatal) return;
          if (d.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
          else if (d.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
          else setErro(true);
        });
        hls.loadSource(src);
        hls.attachMedia(v);
        v.play().catch(() => {});
      }).catch(() => { if (vivo) setErro(true); });
    }
    return () => {
      vivo = false;
      if (hls) hls.destroy();
      v.removeAttribute('src');
      v.load();
    };
  }, [url]);

  // Esc fecha; o mapa por baixo não rola enquanto o vídeo está aberto. O
  // onFechar mora num ref: um novo render da tela não pode sair da tela cheia.
  const fechar = useRef(onFechar);
  fechar.current = onFechar;
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => { if (e.key === 'Escape' && !document.fullscreenElement) fechar.current(); };
    const mudou = () => setCheia(!!document.fullscreenElement);
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', tecla);
    document.addEventListener('fullscreenchange', mudou);
    return () => {
      window.removeEventListener('keydown', tecla);
      document.removeEventListener('fullscreenchange', mudou);
      document.body.style.overflow = antes;
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  const telaCheia = () => {
    const c = caixa.current as any;
    const v = video.current as any;
    if (document.fullscreenElement) { document.exitFullscreen().catch(() => {}); return; }
    if (c?.requestFullscreen) c.requestFullscreen().catch(() => v?.webkitEnterFullscreen?.());
    else if (c?.webkitRequestFullscreen) c.webkitRequestFullscreen();
    else if (v?.webkitEnterFullscreen) v.webkitEnterFullscreen();
  };

  return createPortal(
    <div className="tv-player" ref={caixa} role="dialog" aria-modal="true" aria-label={titulo} data-player>
      <video ref={video} className="tv-player-video" controls playsInline preload="auto"
        poster={capaDoVideo(url) || undefined} data-player-video />
      <div className="tv-player-barra">
        <button type="button" className="tv-player-botao" onClick={onFechar} aria-label="Fechar o vídeo" data-player-fechar>Fechar</button>
        <span className="tv-player-titulo">{titulo}</span>
        <button type="button" className="tv-player-botao" onClick={telaCheia} data-player-telacheia>{cheia ? 'Sair da tela cheia' : 'Tela cheia'}</button>
      </div>
      {erro && <div className="tv-player-erro" data-player-erro>Não consegui tocar o vídeo agora. Feche e tente de novo em instantes.</div>}
    </div>,
    document.body,
  );
}
